// 1:1 port from age-of-mcp v3 (market.ts). The flattened part literals are
// reconstructed from the original helper functions + loops; geometry is
// parity-gated identical.
//
// v3 market — THE STRIPED AWNING, read from above. Footprint-native 3x3: a
// tall plaster trading hall at the back under a banner-red gable (ROOF_TRADE),
// and the trademark out front: a BOLD red/white STRIPED awning over the
// entrance, with goods displayed in the OPEN forecourt. A gold coin emblem over
// the door and a tall pennant pole complete the landmark. Entrance faces +z.

import { defineModel, styleV3, type Part } from "../../lib";

const { PLASTER, TIMBER, WOOD, WOOD_DK, CANVAS, DOOR, ROOF_TRADE, ROOF_FOOD, ROOF_PRECIOUS, GEM, box, cyl, slab, poles, gable } = styleV3;

const BASE   = 0.12;
const HZ     = -0.45;          // hall centre z (toward the back of the plot)
const HALL_W = 2.0;
const HALL_D = 1.3;
const WALL_H = 1.2;
const FRONT  = HZ + HALL_D / 2;   // 0.2 — the hall's entrance face

// The plaster hall body, `h` tall on the foundation.
function hall(h: number): Part {
  return box(HALL_W, h, HALL_D, PLASTER, [0, BASE + h / 2, HZ]);
}

// The red/white striped awning: five tilted cloth segments side by side.
function awning(): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < 5; i++) {
    out.push(box(0.4, 0.045, 0.6, i % 2 ? CANVAS : ROOF_TRADE,
      [-0.8 + i * 0.4, BASE + 1.03, FRONT + 0.32], [0.35, 0, 0]));
  }
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(2.94, 2.94)];
  if (s === 1) return [slab(2.94, 2.94), hall(0.5), ...poles(2.8, 2.8, 0.9)];
  if (s === 2) return [slab(2.94, 2.94), hall(WALL_H), ...poles(2.8, 2.8, 1.7)];

  const top = BASE + WALL_H;          // 1.32 — the eave line
  return [
    slab(2.94, 2.94),
    hall(WALL_H),
    // Banner-red gable over the hall — ridge along x. ONE part.
    gable(1.5, 2.2, 0.7, ROOF_TRADE, 0, top, 0, "x"),
    // Wide entrance with a gold coin emblem above the door.
    box(0.6, 0.8, 0.06, DOOR, [0, BASE + 0.4, FRONT + 0.01]),
    cyl(0.16, 0.16, 0.05, 12, ROOF_PRECIOUS, [0, BASE + 1.05, FRONT + 0.03], [Math.PI / 2, 0, 0]),
    // The STRIPED awning on its two posts.
    ...awning(),
    cyl(0.035, 0.035, 0.95, 4, TIMBER, [-0.95, BASE + 0.475, FRONT + 0.55]),
    cyl(0.035, 0.035, 0.95, 4, TIMBER, [0.95, BASE + 0.475, FRONT + 0.55]),
    // Pennant pole on the back corner.
    cyl(0.03, 0.035, 1.9, 5, TIMBER, [-1.15, BASE + 0.95, HZ - 0.45]),
    box(0.04, 0.28, 0.4, ROOF_TRADE, [-1.15, BASE + 1.72, HZ - 0.24]),
    // Goods on display in the OPEN forecourt.
    box(0.7, 0.08, 0.4, WOOD,    [-0.7, BASE + 0.3, 0.95]),
    box(0.12, 0.26, 0.3, WOOD_DK, [-0.7, BASE + 0.13, 0.95]),
    box(0.18, 0.14, 0.18, GEM,       [-0.85, BASE + 0.41, 0.95]),
    box(0.18, 0.14, 0.18, ROOF_FOOD, [-0.55, BASE + 0.41, 0.95]),
    box(0.3, 0.3, 0.3, WOOD_DK,   [0.75, BASE + 0.15, 0.9]),
    box(0.24, 0.24, 0.24, WOOD,   [1.02, BASE + 0.12, 0.68]),
    cyl(0.15, 0.14, 0.32, 8, WOOD_DK, [0.5, BASE + 0.16, 1.05]),
  ];
}

export const marketV3 = defineModel({
  id: "market-v3",
  tiles: { w: 3, h: 3 },
  batchId: "45-optimized",
  name: "Market (v3)",
  tags: ["building","market","low-poly","age-of-mcp","v3"],
  summary: "Low-poly market.",
  description: "A grand trading hall where players buy and sell goods.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
